import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import VaccinesIcon from '@mui/icons-material/Vaccines'
import API from '../../../../services/API'
import useIsMobile from '../../../../helpers/useIsMobile'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import EmptyState from '../../../../components/EmptyState/EmptyState'
import { StatTile } from '../../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import { useMinuteTick } from './MyPreparedMedicines'
import { bagName, doseSummary, expiryLabel, expiryTone } from './preparedHelpers'

const STATUS_COLORS = {
  held: 'info',
  administered: 'success',
  discarded: 'default',
}

const MOBILE_HIDDEN_COLUMNS = {
  preparedBy: false,
  fromLocation: false,
  batch: false,
}

// Governance view of drawn-up syringes across the organisation - held
// syringes past their expiry are highlighted so they can be chased.
const GovernancePreparedMedicines = () => {
  useMinuteTick()
  const isMobile = useIsMobile()
  const [status, setStatus] = useState('held')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState('')
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({})

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? MOBILE_HIDDEN_COLUMNS : {})
  }, [isMobile])

  useEffect(() => {
    setLoading(true)
    setFetchError('')
    API.get(`/medsmx/prepared?status=${status}`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) setRows(res.data)
        else if (res.status === 401 || res.status === 403)
          setFetchError('Only the Governance team can view all drawn-up medicines.')
        else setFetchError('Unable to load drawn-up medicines.')
      })
      .catch(() => setFetchError('Unable to load drawn-up medicines.'))
      .finally(() => setLoading(false))
  }, [status])

  const now = moment()
  const isOverdue = (row) =>
    row.status === 'held' && row.expiresAt && moment(row.expiresAt).isBefore(now)
  const heldRows = rows.filter((row) => row.status === 'held')
  const overdue = heldRows.filter(isOverdue)
  const controlled = heldRows.filter((row) => row.isControlled)

  // Rebuilt every render (the minute tick) so expiry countdowns stay live
  const columns = [
    {
      field: 'medicineName',
      headerName: 'Medicine',
      minWidth: 160,
      flex: 1,
      renderCell: ({ row }) => (
        <Stack justifyContent="center" height="100%" minWidth={0}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {row.medicineName}
            {row.isControlled ? ' (CD)' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {doseSummary(row)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'heldBy',
      headerName: 'Held by',
      minWidth: 140,
      valueGetter: (value, row) => row.heldBy?.name ?? '',
    },
    {
      field: 'preparedBy',
      headerName: 'Drawn up',
      minWidth: 170,
      valueGetter: (value, row) => moment(row.preparedAt).unix(),
      renderCell: ({ row }) =>
        `${moment(row.preparedAt).format('DD/MM HH:mm')} · ${row.preparedBy?.name ?? ''}`,
    },
    {
      field: 'fromLocation',
      headerName: 'From bag',
      minWidth: 150,
      valueGetter: (value, row) => bagName(row.fromLocation),
    },
    {
      field: 'batch',
      headerName: 'Batch',
      minWidth: 110,
      valueGetter: (value, row) => (row.batchNumbers ?? []).join(', '),
    },
    {
      field: 'expiresAt',
      headerName: 'Expiry',
      minWidth: 170,
      valueGetter: (value, row) => moment(row.expiresAt).unix(),
      renderCell: ({ row }) =>
        row.status === 'held' ? (
          <Chip
            size="small"
            color={expiryTone(row.expiresAt)}
            variant={isOverdue(row) ? 'filled' : 'outlined'}
            label={expiryLabel(row.expiresAt)}
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
        ) : (
          moment(row.expiresAt).format('DD/MM/YYYY HH:mm')
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={isOverdue(row) ? 'Overdue' : row.status}
          color={isOverdue(row) ? 'error' : STATUS_COLORS[row.status] ?? 'default'}
          sx={{ borderRadius: 1, textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      minWidth: 200,
      flex: 1,
      valueGetter: (value, row) =>
        row.status === 'discarded'
          ? `Discarded ${moment(row.discardedAt).format('DD/MM HH:mm')}: ${row.discardReason ?? ''}`
          : row.administrations?.length
            ? `${row.administrations.length} dose${row.administrations.length === 1 ? '' : 's'} given`
            : '',
    },
  ]

  return (
    <Stack gap={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        gap={1}
      >
        <Box>
          <Typography variant="title2" component="div">
            Drawn-up Medicines
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Syringes drawn up in advance from drug bags, who holds them and
            when they expire
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={status}
          onChange={(e, value) => value && setStatus(value)}
        >
          <ToggleButton value="held">Held</ToggleButton>
          <ToggleButton value="administered">Given</ToggleButton>
          <ToggleButton value="discarded">Discarded</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {loading && <LinearProgress />}
      {fetchError && <Alert severity="error">{fetchError}</Alert>}

      {!loading && !fetchError && (
        <>
          {(status === 'held' || status === 'all') && (
            <Box
              display="grid"
              gap={1}
              gridTemplateColumns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }}
            >
              <StatTile label="Held now" value={String(heldRows.length)} tone="info" />
              <StatTile
                label="Overdue"
                value={String(overdue.length)}
                tone={overdue.length ? 'error' : 'success'}
                caption={overdue.length ? 'Past expiry - chase the holder' : 'None past expiry'}
              />
              <StatTile
                label="Controlled drugs held"
                value={String(controlled.length)}
                tone={controlled.length ? 'warning' : undefined}
              />
            </Box>
          )}
          {overdue.length > 0 && (
            <Alert severity="error">
              {overdue.length} held syringe{overdue.length === 1 ? ' is' : 's are'} past
              expiry and should be discarded:{' '}
              {overdue
                .map((row) => `${row.medicineName} (${row.heldBy?.name ?? 'unknown'})`)
                .join(', ')}
            </Alert>
          )}
          {rows.length === 0 ? (
            <EmptyState
              icon={VaccinesIcon}
              title={status === 'held' ? 'No syringes are held right now' : 'Nothing to show'}
              description="When clinicians draw up medicines in advance from a drug bag, they appear here until they are given or discarded."
              sx={{ boxSizing: 'border-box' }}
            />
          ) : (
            <CustomDataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row._id}
              maxHeight={640}
              hideFilters
              rowHeight={56}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              disableRowSelectionOnClick
              getRowClassName={({ row }) => (isOverdue(row) ? 'prepared-overdue' : '')}
              sx={{
                '& .prepared-overdue': {
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(244, 67, 54, 0.12)'
                      : 'rgba(244, 67, 54, 0.06)',
                },
              }}
            />
          )}
        </>
      )}
    </Stack>
  )
}

export default GovernancePreparedMedicines
